import { Facebook, Twitter, Linkedin, Send, Link as LinkIcon, Share2 } from "lucide-react";
import { toast } from "sonner";

export default function ShareButtons({ url, title, description, compact = false }) {
  const fullUrl = url || (typeof window !== "undefined" ? window.location.href : "");
  const encUrl = encodeURIComponent(fullUrl);
  const encTitle = encodeURIComponent(title || "");

  const shares = [
    { id: "telegram", label: "Telegram", icon: Send, color: "bg-[#229ED9] hover:bg-[#1989B8]", href: `https://t.me/share/url?url=${encUrl}&text=${encTitle}` },
    { id: "facebook", label: "Facebook", icon: Facebook, color: "bg-[#1877F2] hover:bg-[#0F65D9]", href: `https://www.facebook.com/sharer/sharer.php?u=${encUrl}` },
    { id: "twitter", label: "X / Twitter", icon: Twitter, color: "bg-black hover:bg-slate-800", href: `https://twitter.com/intent/tweet?url=${encUrl}&text=${encTitle}` },
    { id: "linkedin", label: "LinkedIn", icon: Linkedin, color: "bg-[#0A66C2] hover:bg-[#0856A3]", href: `https://www.linkedin.com/sharing/share-offsite/?url=${encUrl}` },
  ];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Could not copy");
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ url: fullUrl, title, text: description });
      } catch (e) {
        // AbortError is expected when user dismisses the share sheet.
        if (e?.name !== "AbortError") console.warn("native share failed", e);
      }
    } else {
      copy();
    }
  };

  if (compact) {
    return (
      <div data-testid="share-buttons-compact" className="flex items-center gap-2 flex-wrap">
        {shares.map((s) => (
          <a key={s.id} href={s.href} target="_blank" rel="noreferrer" data-testid={`share-${s.id}`}
             className={`w-9 h-9 rounded-full ${s.color} text-white flex items-center justify-center press-scale transition-colors`}
             title={`Share on ${s.label}`}>
            <s.icon className="w-4 h-4" />
          </a>
        ))}
        <button onClick={copy} data-testid="share-copy" className="w-9 h-9 rounded-full bg-slate-200 hover:bg-slate-300 text-slate-700 flex items-center justify-center press-scale" title="Copy link">
          <LinkIcon className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div data-testid="share-buttons" className="bg-white border border-slate-200 rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-3">
        <Share2 className="w-4 h-4 text-cm-accent" />
        <h3 className="font-display font-bold text-cm-primary">Share this article</h3>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {shares.map((s) => (
          <a key={s.id} href={s.href} target="_blank" rel="noreferrer" data-testid={`share-${s.id}`}
             className={`flex items-center gap-2 px-4 py-2.5 rounded-xl ${s.color} text-white text-sm font-medium press-scale transition-colors`}>
            <s.icon className="w-4 h-4" /> {s.label}
          </a>
        ))}
        <button onClick={copy} data-testid="share-copy" className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-cm-primary text-sm font-medium press-scale">
          <LinkIcon className="w-4 h-4" /> Copy link
        </button>
        <button onClick={nativeShare} data-testid="share-native" className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-cm-primary hover:bg-cm-accent text-white text-sm font-medium press-scale sm:col-span-3">
          <Share2 className="w-4 h-4" /> More…
        </button>
      </div>
    </div>
  );
}
